import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import VisibilityIcon from "@mui/icons-material/Visibility";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { formatDbDateTime as formatDateTimePreserveNaive } from "utils/dateFormatter";
import { getPriorityInfo, getStatusInfo } from "layouts/tickets-view/data/ticketViewData";

const fieldLabelSx = {
  display: "flex",
  flexDirection: "column",
  gap: 0.25,
  alignItems: "flex-start",
};

function TicketField({ label, children }) {
  return (
    <MDBox sx={fieldLabelSx}>
      <MDTypography variant="caption" fontWeight="medium" color="secondary">
        {label}
      </MDTypography>
      {children}
    </MDBox>
  );
}

TicketField.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

function PriorityChip({ level }) {
  const priority = getPriorityInfo(level);
  return (
    <MDBox
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      px={1.5}
      py={0.5}
      borderRadius="lg"
      sx={{
        backgroundColor: priority.bgColor,
        color: priority.textColor,
        minWidth: "80px",
        textAlign: "center",
      }}
    >
      <MDTypography variant="caption" fontWeight="medium">
        {priority.label}
      </MDTypography>
    </MDBox>
  );
}

PriorityChip.propTypes = {
  level: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

function StatusChip({ resolved }) {
  const status = getStatusInfo(resolved);
  return (
    <MDBox
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      px={1.5}
      py={0.5}
      borderRadius="lg"
      sx={{
        backgroundColor: status.bgColor,
        color: status.textColor,
        minWidth: "80px",
        textAlign: "center",
      }}
    >
      <MDTypography variant="caption" fontWeight="medium">
        {status.label}
      </MDTypography>
    </MDBox>
  );
}

StatusChip.propTypes = {
  resolved: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

function CustomerTicketsMobileCards({
  tickets,
  onViewTicket,
  hideAssignee = false,
  showUnreadReplies = false,
}) {
  return (
    <MDBox display="flex" flexDirection="column" gap={2}>
      {tickets.map((ticket) => (
        <Card
          key={ticket.ticket_id}
          variant="outlined"
          sx={{ overflow: "hidden" }}
        >
          <MDBox p={2}>
            <MDBox display="flex" alignItems="flex-start" justifyContent="space-between" gap={1}>
              <MDBox minWidth={0} flex={1}>
                <MDTypography variant="caption" color="secondary" fontWeight="medium">
                  Ticket ID
                </MDTypography>
                <MDTypography variant="button" fontWeight="bold">
                  {ticket.ticket_id}
                </MDTypography>
              </MDBox>
              <MDBox display="flex" alignItems="center" gap={0.5} flexShrink={0}>
                {showUnreadReplies && Number(ticket.unread_customer_replies || 0) > 0 ? (
                  <Chip
                    label={`Unread (${ticket.unread_customer_replies})`}
                    color="warning"
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                ) : null}
                <MDButton
                  variant="text"
                  color="info"
                  onClick={() => onViewTicket(ticket)}
                  sx={{ minWidth: "auto", p: "6px" }}
                >
                  <VisibilityIcon />
                </MDButton>
              </MDBox>
            </MDBox>

            <Divider sx={{ my: 1.5 }} />

            <MDBox display="flex" flexDirection="column" gap={1.25}>
              <TicketField label="Department">
                <MDTypography variant="body2" color="text">
                  {ticket.department || "—"}
                </MDTypography>
              </TicketField>
              <TicketField label="Level">
                <PriorityChip level={ticket.level} />
              </TicketField>
              <TicketField label="Status">
                <StatusChip resolved={ticket.resolved} />
              </TicketField>
              {!hideAssignee ? (
                <TicketField label="Current User">
                  <MDTypography variant="body2" color="text">
                    {ticket.current_user || "Unassigned"}
                  </MDTypography>
                </TicketField>
              ) : null}
              <TicketField label="Created (GMT+2)">
                <MDTypography variant="body2" color="text">
                  {formatDateTimePreserveNaive(ticket.create_datetime)}
                </MDTypography>
              </TicketField>
            </MDBox>
          </MDBox>
        </Card>
      ))}
    </MDBox>
  );
}

CustomerTicketsMobileCards.propTypes = {
  tickets: PropTypes.arrayOf(PropTypes.object).isRequired,
  onViewTicket: PropTypes.func.isRequired,
  hideAssignee: PropTypes.bool,
  showUnreadReplies: PropTypes.bool,
};

export default CustomerTicketsMobileCards;
